import { useMutation } from '@apollo/client';
import type { FetchApiExportResponse } from '@generated/graphql';
import type { Configuration } from '@ovComponents/3-pattern/downloadButton/configurationModal';
import type { Column } from '@ovComponents/3-pattern/downloadButton/downloadButton';
import { EXPORT_COMPLETION_OPEN_DELAY_MS } from '@ovComponents/3-pattern/exportPoll/constants';
import { createExportPoll } from '@ovComponents/3-pattern/exportPoll/createExportPoll';
import { safeWindowOpen } from '@utils/urlSanitization';
import { useSnackbar } from 'hephaestus/web';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  API_EXPORT_STATES,
  EXPORT_API_REPORT,
  FETCH_API_EXPORT,
} from '../utils/apiExportOperations';

type FetchApiExportPollData = {
  fetchApiExport?: FetchApiExportResponse;
};

const selectApiExport = (data: FetchApiExportPollData | undefined) =>
  data?.fetchApiExport?.apiExport;

const useApiExportPoll = createExportPoll<
  FetchApiExportPollData,
  { input: { apiExportId: string } }
>({
  query: FETCH_API_EXPORT,
  selectExport: selectApiExport,
  states: API_EXPORT_STATES,
});

export type UseDownloadButtonConfigsParams = {
  gql: string;
  filter: Record<string, any>;
  additionalFilter?: Record<string, any>;
  columns: Column[];
  fileName?: string;
  queryPath: string;
  datasetPath: string;
  sortField?: string;
  sortDesc?: boolean;
  perPage?: number;
};

export type UseDownloadButtonConfigsResult = {
  onExport: (configuration?: Configuration) => void;
  activeExportId: string | null;
  dismissProgressDialog: () => void;
  progress: number;
};

export function useDownloadButtonConfigs({
  gql,
  filter,
  additionalFilter,
  columns,
  fileName,
  queryPath,
  datasetPath,
  sortField,
  sortDesc,
  perPage,
}: UseDownloadButtonConfigsParams): UseDownloadButtonConfigsResult {
  const [activeExportId, setActiveExportId] = useState<string | null>(null);
  const { t } = useTranslation('components');
  const { enqueueSnackbar } = useSnackbar();
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelCompletionTimer = useCallback(() => {
    if (completionTimerRef.current !== null) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelCompletionTimer(), [cancelCompletionTimer]);

  const finishExport = useCallback(
    (fileUrl: string) => {
      setActiveExportId(null);
      cancelCompletionTimer();
      completionTimerRef.current = setTimeout(() => {
        completionTimerRef.current = null;
        safeWindowOpen(fileUrl, {
          target: '_blank',
          onError: (errorMsg) => {
            enqueueSnackbar(errorMsg, { variant: 'error' });
          },
        });
      }, EXPORT_COMPLETION_OPEN_DELAY_MS);
    },
    [cancelCompletionTimer, enqueueSnackbar],
  );

  // No cancelCompletionTimer() here: the completion timer is only ever set
  // by finishExport (a successful export), and the poll's success/failure
  // callbacks are mutually exclusive (see useExportPoll's handledRef guard),
  // so a pending timer can never be running when this fires.
  const failExport = useCallback(() => {
    enqueueSnackbar(t('downloadButton.failedDownloadMessage'), {
      variant: 'error',
    });
    setActiveExportId(null);
  }, [enqueueSnackbar, t]);

  const [exportApiReport] = useMutation(EXPORT_API_REPORT);

  const { progress } = useApiExportPoll({
    variables: { input: { apiExportId: activeExportId ?? '' } },
    enabled: activeExportId !== null,
    onComplete: finishExport,
    onFail: failExport,
  });

  // No cancelCompletionTimer() here either: the dialog already closes
  // (activeExportId -> null) synchronously in finishExport/failExport before
  // the completion timer exists, so this can't be clicked while one is
  // pending.
  const dismissProgressDialog = useCallback(() => {
    setActiveExportId(null);
  }, []);

  const onExport = useCallback(
    async (configuration?: Configuration) => {
      cancelCompletionTimer();
      setActiveExportId(null);

      try {
        const { data } = await exportApiReport({
          variables: {
            input: {
              gql,
              filter: configuration?.useAdditionalFilter
                ? { ...filter, ...additionalFilter }
                : filter,
              queryPath,
              datasetPath,
              columns: configuration?.selectedColumns ?? columns,
              fileName,
              sortField,
              sortDesc,
              perPage,
            },
          },
        });

        const apiExportId = data?.exportApiReport?.apiExport?.id;
        if (!apiExportId) {
          failExport();
          return;
        }

        setActiveExportId(apiExportId);
      } catch {
        failExport();
      }
    },
    [
      additionalFilter,
      cancelCompletionTimer,
      columns,
      datasetPath,
      exportApiReport,
      failExport,
      fileName,
      filter,
      gql,
      perPage,
      queryPath,
      sortDesc,
      sortField,
    ],
  );

  return {
    onExport,
    activeExportId,
    dismissProgressDialog,
    progress,
  };
}
